// AstroCoach, 12-week self-knowledge journey
// Route: /astro-coach  (week content opens inline via state)

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, CheckCircle2, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAstroCoach, WeekContent } from '../hooks/useAstroCoach';
import { WeekContentView } from '../components/astro-coach/WeekContentView';
import { TextSkeleton } from '../components/shared/TextSkeleton';

// ─── Constants ───────────────────────────────────────────────────────────────

const WEEKS_META = [
  { n: 1,  icon: '☀️', planet: 'Sol',        title: 'Quem Sou Eu?',             subtitle: 'O Chamado do Sol'        },
  { n: 2,  icon: '🌙', planet: 'Lua',        title: 'A Maré das Emoções',       subtitle: 'A Linguagem da Lua'      },
  { n: 3,  icon: '💬', planet: 'Mercúrio',   title: 'A Teia da Mente',          subtitle: 'O Dom de Mercúrio'       },
  { n: 4,  icon: '💖', planet: 'Vênus',      title: 'O Jardim do Coração',      subtitle: 'A Dança de Vênus'        },
  { n: 5,  icon: '🔥', planet: 'Marte',      title: 'O Fogo da Ação',           subtitle: 'A Força de Marte'        },
  { n: 6,  icon: '🌟', planet: 'Júpiter',    title: 'O Horizonte que Expande',  subtitle: 'A Abundância de Júpiter' },
  { n: 7,  icon: '⏳', planet: 'Saturno',    title: 'O Arquiteto da Alma',      subtitle: 'A Sabedoria de Saturno'  },
  { n: 8,  icon: '⚡', planet: 'Urano',      title: 'O Relâmpago do Despertar', subtitle: 'A Revolução de Urano'    },
  { n: 9,  icon: '🌊', planet: 'Netuno',     title: 'O Oceano Interior',        subtitle: 'A Magia de Netuno'       },
  { n: 10, icon: '🦋', planet: 'Plutão',     title: 'O Fênix que Renasce',      subtitle: 'O Poder de Plutão'       },
  { n: 11, icon: '🧭', planet: 'Nodo Norte', title: 'O Caminho da Alma',        subtitle: 'A Bússola do Destino'    },
  { n: 12, icon: '✨', planet: 'Integração', title: 'O Mapa Completo',          subtitle: 'A Arte da Integração'    },
];

const WEEK_COLORS: Record<number, string> = {
  1: '#f59e0b', 2: '#c084fc', 3: '#60a5fa', 4: '#f472b6',
  5: '#f97316', 6: '#fbbf24', 7: '#94a3b8', 8: '#a78bfa',
  9: '#38bdf8', 10: '#c084fc', 11: '#34d399', 12: '#fde68a',
};

// ─── Week card ────────────────────────────────────────────────────────────────

function WeekCard({
  meta,
  weekStatus,
  generating,
  onOpen,
}: {
  meta: typeof WEEKS_META[0];
  weekStatus: import('../hooks/useAstroCoach').WeekStatus | undefined;
  generating: boolean;
  onOpen: () => void;
}) {
  const color    = WEEK_COLORS[meta.n] ?? '#fbbf24';
  const status   = weekStatus?.status ?? 'pending';
  const unlocked = weekStatus?.unlocked ?? false;

  return (
    <button
      onClick={unlocked ? onOpen : undefined}
      disabled={!unlocked}
      style={{
        display: 'flex', alignItems: 'center', gap: '1rem',
        width: '100%', textAlign: 'left',
        background: 'var(--bg-surface)',
        border: `1px solid ${unlocked ? color + '44' : 'var(--border-subtle)'}`,
        borderLeft: unlocked ? `4px solid ${color}` : '4px solid var(--border-subtle)',
        borderRadius: 'var(--radius-card)',
        padding: '1rem 1.25rem',
        cursor: unlocked ? 'pointer' : 'not-allowed',
        opacity: unlocked ? 1 : 0.55,
        transition: 'border-color 0.2s, opacity 0.2s',
      }}
    >
      {/* Icon */}
      <span style={{ fontSize: '1.75rem', flexShrink: 0 }}>{meta.icon}</span>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 'var(--text-caption)', color, fontWeight: 600, marginBottom: '0.15rem' }}>
          Semana {meta.n} · {meta.planet}
        </p>
        <p style={{ fontSize: 'var(--text-body)', color: 'var(--text-primary)', fontWeight: 600, marginBottom: '0.15rem' }}>
          {meta.title}
        </p>
        <p style={{ fontSize: 'var(--text-small)', color: 'var(--text-muted)' }}>{meta.subtitle}</p>
      </div>

      {/* Status badge */}
      <div style={{ flexShrink: 0 }}>
        {!unlocked && <Lock size={16} color="var(--text-muted)" />}
        {unlocked && status === 'completed' && <CheckCircle2 size={18} color="#4ade80" />}
        {unlocked && status === 'processing' && <Loader2 size={18} color={color} className="animate-spin" />}
        {unlocked && generating && <Loader2 size={18} color={color} className="animate-spin" />}
        {unlocked && status === 'error' && <AlertCircle size={18} color="#f87171" />}
        {unlocked && (status === 'pending' || status === undefined) && !generating && (
          <span style={{ fontSize: 'var(--text-caption)', color, fontWeight: 600 }}>Iniciar →</span>
        )}
      </div>
    </button>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AstroCoach() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { status, loading, error, getWeekContent } = useAstroCoach();

  const [activeContent, setActiveContent] = useState<WeekContent | null>(null);
  const [generatingWeek, setGeneratingWeek] = useState<number | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [preparingChart, setPreparingChart] = useState(false);

  // Auto-calculate birth chart the first time if it hasn't been done yet
  useEffect(() => {
    if (!profile?.birth_date || status !== null) return;
    setPreparingChart(true);
    import('../lib/supabase').then(({ supabase }) =>
      supabase.functions.invoke('birth-chart', {})
        .finally(() => setPreparingChart(false))
    );
  }, [profile?.birth_date, status]);

  const handleOpenWeek = useCallback(async (weekNumber: number) => {
    setGenError(null);
    setGeneratingWeek(weekNumber);
    try {
      const res = await getWeekContent(weekNumber);
      if (res?.content) setActiveContent(res.content);
    } catch (e: unknown) {
      setGenError(e instanceof Error ? e.message : 'Erro ao carregar semana. Tente novamente.');
    } finally {
      setGeneratingWeek(null);
    }
  }, [getWeekContent]);

  const completed  = status?.summary.completed  ?? 0;
  const total      = 12;
  const pct        = Math.round((completed / total) * 100);

  return (
    <div className="page-container">

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="page-title">Astro Coach</h1>
        <p className="page-subtitle">Jornada de Autoconhecimento, 12 Semanas</p>
      </div>

      {/* Progress bar */}
      {!loading && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg-surface)', borderRadius: 'var(--radius-card)', border: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: 'var(--text-small)', color: 'var(--text-muted)' }}>
              {completed}/{total} semanas concluídas
            </span>
            <span style={{ fontSize: 'var(--text-small)', color: 'var(--text-muted)' }}>{pct}%</span>
          </div>
          <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg,#f59e0b,#c084fc)', borderRadius: 3, transition: 'width 0.5s' }} />
          </div>
          {status?.next_unlock_date && (
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              Próxima semana disponível em {new Date(status.next_unlock_date).toLocaleDateString('pt-BR')}
            </p>
          )}
        </div>
      )}

      {/* No subscription notice */}
      {!loading && !status?.subscription && !status?.is_admin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)', marginBottom: '1.5rem' }}>
          <Sparkles size={18} color="#fbbf24" />
          <p style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)', flex: 1 }}>
            Para iniciar sua jornada, entre em contato com nosso suporte para ativar o Astro Coach.
          </p>
        </div>
      )}

      {/* Error */}
      {(error || genError) && (() => {
        const msg = error ?? genError ?? '';
        const isBirthDate = msg.toLowerCase().includes('nascimento');
        return (
          <div style={{ padding: '1rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-card)', marginBottom: '1rem' }}>
            <p style={{ color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: isBirthDate ? '0.75rem' : 0 }}>{msg}</p>
            {isBirthDate && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => navigate('/profile')}
              >
                Preencher data de nascimento →
              </button>
            )}
          </div>
        );
      })()}

      {/* Week list */}
      {(loading || preparingChart) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {preparingChart && (
            <p style={{ fontSize: 'var(--text-small)', color: 'var(--petrol)', textAlign: 'center', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
              <Loader2 size={14} className="animate-spin" /> Preparando seu mapa natal...
            </p>
          )}
          {Array.from({ length: 6 }).map((_, i) => <div key={i} style={{ padding: '1rem', background: 'var(--bg-surface)', borderRadius: 'var(--radius-card)' }}><TextSkeleton lines={2} /></div>)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {WEEKS_META.map(meta => (
            <WeekCard
              key={meta.n}
              meta={meta}
              weekStatus={status?.weeks.find(w => w.week_number === meta.n)}
              generating={generatingWeek === meta.n}
              onOpen={() => handleOpenWeek(meta.n)}
            />
          ))}
        </div>
      )}

      {/* Week content modal */}
      {activeContent && (
        <WeekContentView
          content={activeContent}
          onClose={() => setActiveContent(null)}
        />
      )}
    </div>
  );
}
